import type { SnapshotRangeState } from '@/components/snapshot/SnapshotDateRangePicker';
import { isRangeValid, type DateRange } from '@/api/reportDateRanges';

export const APP_DATE_RANGE_STORAGE_KEY = 'amz.appDateRange.v1';
const STORAGE_VERSION = 1;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

type StoredAppDateRange = {
  v: number;
  current: DateRange;
  previous: DateRange;
  compareEnabled: boolean;
};

function parseDateRange(value: unknown): DateRange | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const startDate = typeof record.startDate === 'string' ? record.startDate : '';
  const endDate = typeof record.endDate === 'string' ? record.endDate : '';
  if (!ISO_DATE.test(startDate) || !ISO_DATE.test(endDate)) return null;
  const range = { startDate, endDate };
  return isRangeValid(range) ? range : null;
}

export function parseStoredAppDateRange(raw: unknown): SnapshotRangeState | null {
  if (!raw || typeof raw !== 'object') return null;
  const parsed = raw as Partial<StoredAppDateRange>;
  if (parsed.v !== STORAGE_VERSION) return null;
  const current = parseDateRange(parsed.current);
  const previous = parseDateRange(parsed.previous);
  if (!current || !previous) return null;
  return {
    current,
    previous,
    compareEnabled: parsed.compareEnabled !== false,
  };
}

export function loadAppDateRange(): SnapshotRangeState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(APP_DATE_RANGE_STORAGE_KEY);
    if (!raw) return null;
    return parseStoredAppDateRange(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function saveAppDateRange(range: SnapshotRangeState): void {
  if (typeof window === 'undefined') return;
  const payload: StoredAppDateRange = {
    v: STORAGE_VERSION,
    current: range.current,
    previous: range.previous,
    compareEnabled: range.compareEnabled,
  };
  try {
    window.sessionStorage.setItem(APP_DATE_RANGE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable
  }
}
